import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr, fmtMoney, fmtDate } from "../../api";
import { SectionTitle, StatusPill, RichHtml } from "../../components/Atoms";
import { ArrowLeft, Calendar, CheckCircle, Link as LinkIcon, UploadSimple, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AmbassadorTaskDetailPage() {
  const { pid, tid } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [canSubmit, setCanSubmit] = useState(false);  // true only when approved on the project
  const [form, setForm] = useState({ link: "", file_url: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/me/projects/${pid}/detail`);
      setProject(data.project);
      const t = (data.tasks || []).find(x => x.id === tid);
      if (!t) {
        toast.error("Task not found in this project");
        nav(`/me/projects/${pid}`);
        return;
      }
      setTask(t);
      setCanSubmit(!data.can_apply && data.application_status === "Approved");
      setForm({
        link: t.submission?.link || "",
        file_url: t.submission?.file_url || "",
        notes: t.submission?.notes || "",
      });
    } catch (e) {
      toast.error(formatErr(e));
      nav(`/me/projects/${pid}`);
    }
  }, [pid, tid, nav]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, file_url: data.url }));
      toast.success("File uploaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/submissions", { ...form, task_id: tid, project_id: pid });
      toast.success("Submitted! Awaiting review.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  if (!project || !task) return <div className="skeleton h-64" />;

  const er = (task.evidence_required || "both").toLowerCase();
  const need_link = er === "link" || er === "both";
  const need_file = er === "file" || er === "both";
  const proofTypeLabel = er === "link" ? "Link only" : er === "file" ? "File only" : "Link + File";
  const totalReward = (task.reward_amount || 0) + (task.bonus_amount || 0);

  return (
    <div className="space-y-5">
      <button onClick={() => nav(`/me/projects/${pid}`)} className="btn-ghost text-sm flex items-center gap-1.5 w-fit" data-testid="back-to-project">
        <ArrowLeft size={14} weight="bold" /> Back to project
      </button>

      {/* Task header */}
      <div className="glass p-5">
        <div className="text-[10px] uppercase tracking-wider text-muted font-bold">
          <Link to={`/me/projects/${pid}`} className="hover:text-[#22D3EE]">{project.title}</Link> · Task
        </div>
        <div className="text-2xl sm:text-3xl font-black text-white mt-1" data-testid="task-title">{task.title}</div>
        <div className="flex items-center gap-2 flex-wrap mt-2">
          {((task.platforms && task.platforms.length > 0) ? task.platforms : (task.platform ? [task.platform] : [])).map(pl => (
            <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
          ))}
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">{fmtMoney(totalReward)}</span>
          {task.bonus_amount > 0 && <span className="text-[10px] uppercase tracking-wider font-bold text-[#EC4899] bg-[#EC4899]/10 border border-[#EC4899]/30 px-2 py-0.5 rounded-full">+ {fmtMoney(task.bonus_amount)} bonus</span>}
          {task.deadline && <span className="text-xs text-muted flex items-center gap-1"><Calendar size={12} /> Due {fmtDate(task.deadline)}</span>}
          {task.submission && <StatusPill status={task.submission.status} />}
        </div>
      </div>

      {/* Instructions */}
      {task.instructions && (
        <div className="glass p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted mb-2">Detailed Instructions</div>
          <RichHtml html={task.instructions} className="text-sm text-white/90 rich-html" />
        </div>
      )}

      {/* Proof description (admin's instructions for what to attach) */}
      {task.proof_required && (
        <div className="glass p-5 border border-[#22D3EE]/20" style={{ background: "linear-gradient(180deg, rgba(34,211,238,0.06), rgba(15,15,20,0.85))" }}>
          <div className="flex items-center gap-2 mb-2">
            <ClipboardText size={16} weight="bold" className="text-[#22D3EE]" />
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE]">Proof Required</div>
          </div>
          <div className="text-sm text-white whitespace-pre-line">{task.proof_required}</div>
        </div>
      )}

      {/* Feedback (if Rejected) */}
      {task.submission?.feedback && (
        <div className="glass p-4 border border-[#F59E0B]/30" style={{ background: "rgba(245,158,11,0.06)" }} data-testid="task-feedback">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#F59E0B] mb-1">Reviewer Feedback</div>
          <div className="text-sm text-white/90">{task.submission.feedback}</div>
        </div>
      )}

      {/* Inline submission form */}
      {!canSubmit ? (
        <div className="glass p-5 text-sm text-muted" data-testid="task-locked-msg">
          You need to be approved on this project before submitting proof. Head back and apply if you haven&apos;t already.
        </div>
      ) : task.submission?.status === "Approved" ? (
        <div className="glass p-5 text-sm text-[#A3E635] flex items-center gap-2" data-testid="task-approved-msg">
          <CheckCircle size={16} weight="fill" /> This task is already approved. Earnings are queued for payout.
        </div>
      ) : (
        <form onSubmit={submit} className="glass p-5 space-y-4" data-testid="task-submission-form">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <SectionTitle title="Submit your proof" subtitle="Once submitted, the brand admin will review and approve." />
            <div className="rounded-xl bg-[#A3E635]/8 border border-[#A3E635]/20 px-3 py-1.5 text-[11px] text-[#A3E635]">
              Proof type required: <span className="font-bold">{proofTypeLabel}</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {need_link && (
              <div>
                <label className="text-xs uppercase font-bold text-muted">Submission Link <span className="text-[#EC4899]">*</span></label>
                <div className="relative">
                  <input required={need_link} value={form.link} onChange={e => setForm({...form, link:e.target.value})} placeholder="https://instagram.com/p/…" className="input-base pl-9 mt-1" data-testid="submit-link" />
                  <LinkIcon className="absolute left-3 top-1/2 mt-0.5 text-muted" size={16} />
                </div>
              </div>
            )}
            {need_file && (
              <div>
                <label className="text-xs uppercase font-bold text-muted">Upload File <span className="text-[#EC4899]">*</span></label>
                <label className="flex items-center gap-3 mt-1 p-3 rounded-xl bg-white/5 border border-dashed border-white/15 cursor-pointer hover:bg-white/10">
                  <UploadSimple size={18} className="text-[#22D3EE]" />
                  <div className="text-sm text-muted">{form.file_url ? "✓ File attached" : uploading ? "Uploading…" : "Tap to upload"}</div>
                  <input type="file" hidden onChange={uploadFile} data-testid="submit-file" />
                </label>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase font-bold text-muted">Notes</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} className="input-base mt-1" placeholder="Anything the reviewer should know…" data-testid="submit-notes" />
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={submitting || (need_file && !form.file_url)} className="btn-primary flex items-center gap-2" data-testid="submit-proof-btn">
              <CheckCircle size={16} weight="bold" /> {submitting ? "Submitting…" : task.submission ? "Resubmit Proof" : "Submit Proof"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
